import type { ReportListItem } from '../../../utils/reportsClient'
import {
  groupReportsForHistory,
  AGGREGATED_GROUP_ID
} from '../pqrDetails/groupReportsForHistory'
import type { PqrWeldOption } from '../pqrPreview/PqrWeldPickerModal'

const PQR_TAG = 'PQR'

function isVisiblePqr(item: ReportListItem): boolean {
  if (item.tag !== PQR_TAG) return false
  if (typeof item.deletedAt === 'string' && item.deletedAt.trim().length > 0) return false
  return true
}

export function trainKeyFor(weldIds: string[], pqrNumber: string): string {
  const sorted = [...weldIds].map((s) => s.trim()).filter(Boolean).sort()
  return `${sorted.join('|')}|${pqrNumber.trim()}`
}

export interface WpsPqrTrainLastHint {
  pqrNumber: string
  revision: string
  uploadedAt: string
  generatedByEmail: string | null
}

/**
 * One selectable PQR train for the WPS picker — a distinct
 * `(weld scope × PQR No.)` pair that has at least one real generated
 * PDF. Synthetic next-version placeholders are excluded.
 */
export interface WpsPqrTrainOption {
  /** Stable selection id: `${sortedWeldIds}|${pqrNumber}` */
  trainKey: string
  pqrNumber: string
  /** Weld ids in train order (from grouped history). */
  weldIds: string[]
  /** Parallel weld numbers for display (may be blank). */
  weldNumbers: string[]
  lastGeneratedHint: WpsPqrTrainLastHint | null
}

/**
 * Extract pickable PQR trains from live PQR report history. Only
 * trains with at least one non-synthetic report are returned — the
 * user must have generated a PQR before a WPS can be scoped to it.
 */
export function readPqrTrainOptions(
  reports: ReportListItem[],
  weldOptions: PqrWeldOption[]
): WpsPqrTrainOption[] {
  const visible = reports.filter(isVisiblePqr)
  const grouped = groupReportsForHistory({ reports: visible, weldOptions })
  const out: WpsPqrTrainOption[] = []
  const seen = new Set<string>()

  const pushFromWeldGroup = (
    weldIds: string[],
    weldNumbers: string[],
    pqrNumber: string,
    realReports: ReportListItem[]
  ) => {
    if (realReports.length === 0) return
    const key = trainKeyFor(weldIds, pqrNumber)
    if (seen.has(key)) return
    seen.add(key)

    const sortedByTime = [...realReports].sort((a, b) =>
      (b.uploadedAt || '').localeCompare(a.uploadedAt || '')
    )
    const latest = sortedByTime[0]
    const generatedByEmail =
      latest.generatedBy &&
      typeof latest.generatedBy.email === 'string' &&
      latest.generatedBy.email.length > 0
        ? latest.generatedBy.email
        : null

    out.push({
      trainKey: key,
      pqrNumber,
      weldIds,
      weldNumbers,
      lastGeneratedHint: {
        pqrNumber,
        revision: typeof latest.revision === 'string' ? latest.revision : '0',
        uploadedAt: latest.uploadedAt || '',
        generatedByEmail
      }
    })
  }

  for (const wg of grouped.weldGroups) {
    for (const pg of wg.pqrGroups) {
      const real = pg.reports.filter((r) => r.isSynthetic !== true)
      pushFromWeldGroup(wg.weldIds, wg.weldNumbers, pg.pqrNumber, real)
    }
  }

  if (grouped.aggregated) {
    for (const pg of grouped.aggregated.pqrGroups) {
      const real = pg.reports.filter((r) => r.isSynthetic !== true)
      pushFromWeldGroup([], [], pg.pqrNumber, real)
    }
  }

  // Sort by PQR number, then train key for stable UI order.
  out.sort((a, b) => {
    const byPqr = a.pqrNumber.localeCompare(b.pqrNumber, undefined, { numeric: true })
    if (byPqr !== 0) return byPqr
    return a.trainKey.localeCompare(b.trainKey)
  })

  return out
}

function dedupePreserveOrder(ids: ReadonlyArray<string>): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of ids) {
    const t = raw.trim()
    if (!t || seen.has(t)) continue
    seen.add(t)
    out.push(t)
  }
  return out
}

function weldIdsFromReport(report: ReportListItem): string[] {
  if (Array.isArray(report.weldIds) && report.weldIds.length > 0) {
    return report.weldIds.map((w) => w.trim()).filter(Boolean)
  }
  const single = typeof report.weldId === 'string' ? report.weldId.trim() : ''
  return single.length > 0 ? [single] : []
}

/**
 * Union weld ids from live PQR report rows matching the given PQR numbers.
 * Mirrors server `unionWeldIdsFromPqrReports`.
 */
export function unionWeldIdsFromPqrReports(
  reports: ReportListItem[],
  pqrNumbers: ReadonlyArray<string>
): string[] {
  const targets = new Set(
    pqrNumbers.map((p) => p.trim()).filter((p) => p.length > 0)
  )
  if (targets.size === 0) return []

  const out: string[] = []
  const seen = new Set<string>()
  for (const report of reports) {
    if (!isVisiblePqr(report)) continue
    const pn = typeof report.pqrNumber === 'string' ? report.pqrNumber.trim() : ''
    if (!pn || !targets.has(pn)) continue
    for (const wid of weldIdsFromReport(report)) {
      if (seen.has(wid)) continue
      seen.add(wid)
      out.push(wid)
    }
  }
  return out
}

/**
 * Full weld scope for WPS: union from selected trains plus any weld ids
 * stored on matching PQR report metadata.
 */
export function unionWeldIdsForWpsSelection(
  selectedKeys: string[],
  options: WpsPqrTrainOption[],
  reports: ReportListItem[],
  pqrNumbers: string[]
): string[] {
  const fromTrains = unionWeldIdsFromTrains(selectedKeys, options)
  const fromPqr = unionWeldIdsFromPqrReports(reports, pqrNumbers)
  return dedupePreserveOrder([...fromTrains, ...fromPqr])
}

/** Union weld ids from selected train keys, deduped, first-seen order. */
export function unionWeldIdsFromTrains(
  selectedKeys: string[],
  options: WpsPqrTrainOption[]
): string[] {
  const byKey = new Map(options.map((o) => [o.trainKey, o]))
  const out: string[] = []
  const seen = new Set<string>()
  for (const key of selectedKeys) {
    const opt = byKey.get(key)
    if (!opt) continue
    for (const wid of opt.weldIds) {
      const t = wid.trim()
      if (!t || seen.has(t)) continue
      seen.add(t)
      out.push(t)
    }
  }
  return out
}

/** PQR numbers for selected trains, in selection order. */
export function pqrNumbersFromTrains(
  selectedKeys: string[],
  options: WpsPqrTrainOption[]
): string[] {
  const byKey = new Map(options.map((o) => [o.trainKey, o]))
  const out: string[] = []
  const seen = new Set<string>()
  for (const key of selectedKeys) {
    const opt = byKey.get(key)
    if (!opt) continue
    const pn = opt.pqrNumber.trim()
    if (!pn || seen.has(pn)) continue
    seen.add(pn)
    out.push(pn)
  }
  return out
}

export { AGGREGATED_GROUP_ID }
